import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';
import { SearchIcon, EditIcon } from '../components/Icons';

const Users = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const fetchUsers = useCallback(async (page = pagination.page, search = searchTerm, limit = pagination.limit) => {
    try {
      setLoading(true);
      const res = await userService.getUsers({ page, limit, search });
      if (res.success) {
        setUsers(res.data);
        setPagination(prev => ({ 
          ...prev, 
          page: res.pagination.page, 
          total: res.pagination.total, 
          pages: res.pagination.pages 
        }));
      }
    } catch (err) {
      setError('An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => fetchUsers(1, searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm, fetchUsers]);

  const canEdit = hasPermission('users', 'edit');

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'super_admin': return { backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--accent)' };
      case 'admin': return { backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' };
      case 'finance': return { backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)' };
      case 'account_manager': return { backgroundColor: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)' };
      case 'developer': return { backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)' };
      default: return { backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--text-muted)' };
    }
  };

  const formatRole = (role) => {
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="users-page">
      {hasPermission('users', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={() => navigate('/users/new')}>Add User</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper"><SearchIcon /></div>
          <input 
            type="text" 
            className="search-input" 
            placeholder="Search username or email..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>
      </div>

      {loading && users.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading users...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                {['Username', 'Email', 'Role', 'Last Login', '2FA', 'Status', canEdit ? 'Actions' : null].filter(Boolean).map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No users found.</td></tr>
              ) : users.map((u) => (
                <tr key={u._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 600 }}>{u.username}</td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem' }}>{u.email}</td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                    <span style={{ 
                        padding: '0.25rem 0.625rem', 
                        borderRadius: '9999px', fontSize: '0.75rem', 
                        fontWeight: 600, 
                        ...getRoleBadgeStyle(u.role)
                    }}>{formatRole(u.role)}</span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                    <div style={{ fontWeight: 600 }}>{u.last_login_ip || '-'}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ''}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                    <span style={{ 
                        padding: '0.25rem 0.625rem', 
                        borderRadius: '9999px', fontSize: '0.75rem', 
                        fontWeight: 600, 
                        backgroundColor: u.is_two_factor_enabled ? 'rgba(16, 185, 129, 0.1)' : 'rgba(var(--primary-rgb), 0.1)',
                        color: u.is_two_factor_enabled ? 'var(--success)' : 'var(--text-muted)' 
                    }}>{u.is_two_factor_enabled ? 'Enabled' : 'Disabled'}</span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                    <span style={{ 
                        padding: '0.25rem 0.625rem', 
                        borderRadius: '9999px', fontSize: '0.75rem', 
                        fontWeight: 600, 
                        backgroundColor: u.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                        color: u.is_active ? 'var(--success)' : 'var(--error)' 
                    }}>{u.is_active ? 'Active' : 'Inactive'}</span>
                  </td>
                    {canEdit && (
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button className="btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }} onClick={() => navigate(`/users/${u._id}`)}>
                          <EditIcon size={14} />
                          Edit
                        </button>
                      </td>
                    )}
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination pagination={pagination} onPageChange={(pg) => fetchUsers(pg, searchTerm)} onLimitChange={(l) => {
            setPagination(prev => ({ ...prev, limit: l }));
            fetchUsers(1, searchTerm, l);
          }} loading={loading} itemName="users" />
        </div>
      )}
    </div>
  );
};

export default Users;
